import { useEffect, useRef, useState, type ReactNode } from "react";
import { X, ZoomIn, ZoomOut } from "lucide-react";

import { useBackdropDismiss } from "./Modal";

export function ZoomableStillDialog({
  title,
  subtitle,
  cropSrc,
  frameSrc,
  onClose,
}: {
  title: string;
  subtitle?: ReactNode;
  cropSrc?: string | null;
  frameSrc?: string | null;
  onClose: () => void;
}) {
  const canFrame = Boolean(frameSrc);
  const [showFrame, setShowFrame] = useState(false);
  const viewingFrame = canFrame && showFrame;
  const src = viewingFrame ? frameSrc : cropSrc;
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(
    null,
  );

  useEffect(() => {
    setShowFrame(false);
    setScale(1);
    setPan({ x: 0, y: 0 });
  }, [cropSrc, frameSrc]);

  useEffect(() => {
    setScale(1);
    setPan({ x: 0, y: 0 });
  }, [viewingFrame]);

  const clampScale = (v: number) => Math.min(5, Math.max(1, v));

  function zoomBy(delta: number) {
    setScale((s) => {
      const next = clampScale(s + delta);
      if (next === 1) setPan({ x: 0, y: 0 });
      return next;
    });
  }

  function onWheel(e: React.WheelEvent) {
    e.preventDefault();
    zoomBy(e.deltaY < 0 ? 0.2 : -0.2);
  }

  function onPointerDown(e: React.PointerEvent) {
    if (scale <= 1) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!dragRef.current) return;
    setPan({
      x: dragRef.current.panX + (e.clientX - dragRef.current.x),
      y: dragRef.current.panY + (e.clientY - dragRef.current.y),
    });
  }

  function onPointerUp() {
    dragRef.current = null;
  }

  const dismiss = useBackdropDismiss(onClose);

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 grid place-items-center bg-black/85 backdrop-blur-sm p-4"
      {...dismiss}
    >
      <div
        className="bf-gradient-border h-[95vh] w-[96vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bf-gradient-border-inner flex h-full flex-col overflow-hidden p-4">
          <div className="mb-3 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="truncate text-sm font-semibold text-white">{title}</h2>
              {subtitle ? (
                <div className="mt-0.5 text-xs text-bf-muted">{subtitle}</div>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {canFrame && (
                <div
                  role="group"
                  aria-label="Image view"
                  className="mr-1 flex items-center gap-1 rounded-lg border border-white/10 bg-bf-surface/60 p-1"
                >
                  <button
                    type="button"
                    aria-pressed={!viewingFrame}
                    onClick={() => setShowFrame(false)}
                    className={
                      "rounded-md px-2.5 py-1 text-xs font-medium transition " +
                      (!viewingFrame
                        ? "bg-bf-violet/30 text-white"
                        : "text-bf-muted hover:bg-white/5 hover:text-bf-text")
                    }
                  >
                    Crop
                  </button>
                  <button
                    type="button"
                    aria-pressed={viewingFrame}
                    onClick={() => setShowFrame(true)}
                    className={
                      "rounded-md px-2.5 py-1 text-xs font-medium transition " +
                      (viewingFrame
                        ? "bg-bf-violet/30 text-white"
                        : "text-bf-muted hover:bg-white/5 hover:text-bf-text")
                    }
                  >
                    Full frame
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => zoomBy(-0.25)}
                className="rounded p-2 text-bf-muted hover:bg-white/10 hover:text-bf-text"
                aria-label="Zoom out"
              >
                <ZoomOut size={18} />
              </button>
              <span className="min-w-[3rem] text-center text-xs text-bf-muted">
                {Math.round(scale * 100)}%
              </span>
              <button
                type="button"
                onClick={() => zoomBy(0.25)}
                className="rounded p-2 text-bf-muted hover:bg-white/10 hover:text-bf-text"
                aria-label="Zoom in"
              >
                <ZoomIn size={18} />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded p-2 text-bf-muted hover:bg-white/10 hover:text-bf-text"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          <div
            className="relative min-h-0 flex-1 overflow-hidden rounded-md bg-white/[0.03]"
            onWheel={onWheel}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onDoubleClick={() => zoomBy(scale < 2 ? 1 : -1)}
          >
            {src ? (
              <img
                src={src}
                alt={title}
                draggable={false}
                className="h-full w-full select-none object-contain object-center transition-transform duration-100"
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
                  cursor: scale > 1 ? "grab" : "zoom-in",
                }}
              />
            ) : (
              <div className="grid h-full place-items-center text-sm text-bf-muted">
                {viewingFrame ? "Frame unavailable" : "Crop unavailable"}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
